/**
 * The big keyword × position table that dominates RankTab. Receives a fully-prepared
 * list of result rows + the pre-computed baseline lookup; rendering only — no fetches.
 *
 * Sorting: column headers for `Pos`, `Local`, and `Volume` are clickable and cycle
 * through none → asc → desc → none. Null values always sort to the bottom regardless
 * of direction (a pending keyword should never displace a real ranking at the top).
 */
import React, { useMemo, useState } from "react";
import { Loader2, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { fmt } from "../../../lib/format";
import RankDeltaCell from "./RankDeltaCell";
import SerpFeaturesCell from "./SerpFeaturesCell";

const STATIC_SORT_ACCESSORS = {
  position: (r) => r.position,
  local_pack_position: (r) => r.local_pack_position,
  search_volume: (r) => r.search_volume,
};

function buildAccessor(key, getPrev, baselinePosByKw, gscByKw) {
  if (STATIC_SORT_ACCESSORS[key]) return STATIC_SORT_ACCESSORS[key];
  if (key === "delta_last_run") {
    // delta = prevPos − curPos → positive means improved (climbed)
    return (r) => {
      const prev = getPrev?.(r.keyword);
      if (prev == null || r.position == null) return null;
      return prev - r.position;
    };
  }
  if (key === "delta_baseline") {
    return (r) => {
      const base = baselinePosByKw?.get?.(r.keyword);
      if (base == null || r.position == null) return null;
      return base - r.position;
    };
  }
  if (key === "gsc_impressions") {
    return (r) => {
      const stats = gscByKw?.[(r.keyword || "").toLowerCase()];
      return stats?.impressions ?? null;
    };
  }
  return null;
}

function sortRows(rows, key, dir, getPrev, baselinePosByKw, gscByKw) {
  if (!key || !dir) return rows;
  const accessor = buildAccessor(key, getPrev, baselinePosByKw, gscByKw);
  if (!accessor) return rows;
  // Decorate-sort-undecorate so the original index is the tie-breaker —
  // stable sort across browsers, no reshuffling of equal-ranked rows.
  const decorated = rows.map((r, i) => ({ r, i, v: accessor(r) }));
  decorated.sort((a, b) => {
    const av = a.v;
    const bv = b.v;
    const aNull = av == null;
    const bNull = bv == null;
    // Nulls always at the bottom regardless of direction
    if (aNull && bNull) return a.i - b.i;
    if (aNull) return 1;
    if (bNull) return -1;
    if (av === bv) return a.i - b.i;
    return dir === "asc" ? av - bv : bv - av;
  });
  return decorated.map((d) => d.r);
}

function SortableHeader({ label, sortKey, currentKey, currentDir, onClick, title, className = "" }) {
  const isActive = currentKey === sortKey;
  const Icon = !isActive
    ? ChevronsUpDown
    : currentDir === "asc"
      ? ChevronUp
      : ChevronDown;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`rank-sort-${sortKey}`}
      title={title || `Sort by ${label}`}
      className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${
        isActive ? "text-[#1a202c]" : ""
      } ${className}`}
    >
      <span>{label}</span>
      <Icon
        className={`h-3 w-3 transition-opacity ${
          isActive ? "opacity-100 text-[#2c7a7b]" : "opacity-40 group-hover/sort:opacity-80"
        }`}
        strokeWidth={2.5}
      />
    </button>
  );
}

export default function KeywordTable({
  list,
  isLocal,
  fetching,
  baselineLabel,
  baselinePosByKw,
  getPrev,
  onRemoveKeyword,
  removingKw,
  emptyMessage,
  gscByKw,
  latestSnapshotAt,
  latestSerpLocations,
}) {
  // If GSC is configured for this client, surface the impressions column.
  // `gscByKw === null` means GSC isn't set up for the client (or the API failed
  // silently); in that case we hide the column entirely so the layout stays clean.
  const showGsc = gscByKw != null;
  // Column layout: kw(4) + pos(1) + [local(1)] + volume(2) + [gsc_impr(2)] +
  // features(2) + delta_last(2) + delta_baseline(2) + url(3). We keep totals
  // in sync with the header and body loops below.
  const nationalCols = showGsc ? 18 : 16;
  const localCols = showGsc ? 19 : 17;
  const gridCols = isLocal
    ? `repeat(${localCols}, minmax(0, 1fr))`
    : `repeat(${nationalCols}, minmax(0, 1fr))`;
  // Tri-state sort: null → asc → desc → null. Clicking another column resets to asc.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  };
  const sortedList = useMemo(
    () => sortRows(list, sortKey, sortDir, getPrev, baselinePosByKw, gscByKw),
    [list, sortKey, sortDir, getPrev, baselinePosByKw, gscByKw],
  );

  // Compose the "why is this empty?" tooltip shown on the Pos cell for
  // keywords that were crawled but not found. The tooltip resolves the
  // scope-specific SERP location and formats the crawl timestamp so a user
  // can immediately compare against the SERP tab.
  const buildEmptyPosTooltip = (row) => {
    if (row.pending) {
      return "Added recently — will be crawled on the next scheduled rank check. Click 'Check ranks now' to run one immediately.";
    }
    if (!latestSnapshotAt) return "Not found in top 100 organic results.";
    let loc = null;
    if (latestSerpLocations && typeof latestSerpLocations === "object") {
      loc = latestSerpLocations[row.scope || "national"] || latestSerpLocations.national || latestSerpLocations.local;
    } else if (typeof latestSerpLocations === "string") {
      loc = latestSerpLocations;
    }
    const when = new Date(latestSnapshotAt).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const locBit = loc ? ` on the "${loc}" SERP` : "";
    return `Not found in top 100 organic results. Last crawled ${when}${locBit}. Try the SERP tab to compare against a live search.`;
  };
  return (
    <div className="card-soft overflow-hidden">
      <div
        className="grid gap-x-4 px-5 py-3.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className="col-span-4">Keyword</div>
        <div className="col-span-1 flex justify-end">
          <SortableHeader
            label="Pos"
            sortKey="position"
            currentKey={sortKey}
            currentDir={sortDir}
            onClick={handleSort}
          />
        </div>
        {isLocal && (
          <div className="col-span-1 flex justify-end">
            <SortableHeader
              label="Local"
              sortKey="local_pack_position"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              title="Sort by Local Pack position (1–3)"
            />
          </div>
        )}
        <div className="col-span-2 flex justify-end">
          <SortableHeader
            label="Volume"
            sortKey="search_volume"
            currentKey={sortKey}
            currentDir={sortDir}
            onClick={handleSort}
          />
        </div>
        {showGsc && (
          <div className="col-span-2 flex justify-end">
            <SortableHeader
              label="Impr (28d)"
              sortKey="gsc_impressions"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              title="GSC impressions in the last 28 days for this exact query"
            />
          </div>
        )}
        <div className="col-span-2 flex justify-end" title="SERP features present on this keyword's SERP · green = you own the feature">
          Features
        </div>
        <div className="col-span-2 flex justify-end">
          <SortableHeader
            label="vs last run"
            sortKey="delta_last_run"
            currentKey={sortKey}
            currentDir={sortDir}
            onClick={handleSort}
            title="Sort by position change vs previous rank check (asc = biggest drops first)"
          />
        </div>
        <div
          className="col-span-2 flex justify-end"
          title={`Change vs ${baselineLabel || "earliest snapshot"}`}
        >
          <SortableHeader
            label="vs first run"
            sortKey="delta_baseline"
            currentKey={sortKey}
            currentDir={sortDir}
            onClick={handleSort}
            title={`Sort by position change vs ${baselineLabel || "earliest snapshot"} (asc = biggest drops first)`}
          />
        </div>
        <div className="col-span-3 truncate">URL</div>
      </div>
      {fetching ? (
        <div className="p-6 text-center text-sm text-[#a0aec0] font-mono-tight flex items-center justify-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading snapshots…
        </div>
      ) : sortedList.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#a0aec0]" data-testid="rank-empty">
          {emptyMessage ||
            'No snapshots yet. Click "Check ranks now" or wait for the next scheduled run.'}
        </div>
      ) : (
        <div className="divide-y divide-[#edf2f7]">
          {sortedList.map((r, i) => {
            const prevPos = getPrev(r.keyword);
            const delta = prevPos != null && r.position != null ? prevPos - r.position : null;
            const basePos = baselinePosByKw.get(r.keyword) ?? null;
            const baseDelta = basePos != null && r.position != null ? basePos - r.position : null;
            const baseIsNew = basePos == null && r.position != null;
            const baseIsLost = basePos != null && r.position == null;
            const url = r.url || "";
            const urlShort = url.replace(/^https?:\/\//, "");
            const isRemoving = removingKw === r.keyword;
            return (
              <div
                key={i}
                data-testid={`rank-row-${i}`}
                className="grid gap-x-4 px-5 py-3 hover:bg-[#f7fafc] text-sm items-center group"
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className="col-span-4 flex items-center gap-2 min-w-0">
                  <span className="truncate text-[#1a202c]">{r.keyword}</span>
                  {r.pending && (
                    <span
                      data-testid={`rank-pending-${i}`}
                      title="Added recently — will be crawled on the next rank check"
                      className="shrink-0 text-[9px] uppercase tracking-widest font-bold text-[#744210] bg-[#fefcbf]/80 border border-[#f6e05e]/60 px-1.5 py-0.5 rounded-md"
                    >
                      Pending
                    </span>
                  )}
                  <button
                    onClick={(e) => onRemoveKeyword(r.keyword, e)}
                    disabled={isRemoving}
                    data-testid={`rank-remove-${i}`}
                    title={`Stop tracking "${r.keyword}"`}
                    className="shrink-0 h-5 w-5 rounded-full inline-flex items-center justify-center text-[#a0aec0] hover:text-[#c53030] hover:bg-[#fed7d7]/50 disabled:opacity-50 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {isRemoving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <X className="h-3 w-3" strokeWidth={2.4} />
                    )}
                  </button>
                </div>
                <div className="col-span-1 text-right font-mono-tight font-semibold">
                  {r.position != null ? (
                    <span className={r.position <= 10 ? "text-[#38b2ac]" : "text-[#4a5568]"}>{r.position}</span>
                  ) : (
                    <span
                      className="text-[#a0aec0] cursor-help border-b border-dotted border-[#cbd5e0]"
                      data-testid={`rank-empty-pos-${i}`}
                      title={buildEmptyPosTooltip(r)}
                    >
                      —
                    </span>
                  )}
                </div>
                {isLocal && (
                  <div className="col-span-1 text-right font-mono-tight font-semibold" data-testid={`rank-localpack-${i}`}>
                    {r.local_pack_position != null ? (
                      <span className="inline-flex items-center gap-1 text-[#2c7a7b] bg-[#4fd1c5]/10 px-1.5 py-0.5 rounded-md">
                        #{r.local_pack_position}
                      </span>
                    ) : r.local_pack_present ? (
                      <span className="text-[#a0aec0]" title="Local Pack appeared but your domain wasn't in it">—</span>
                    ) : (
                      <span className="text-[#cbd5e0]" title="No Local Pack on this SERP">·</span>
                    )}
                  </div>
                )}
                <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">
                  {fmt(r.search_volume)}
                </div>
                {showGsc && (() => {
                  const stats = gscByKw?.[(r.keyword || "").toLowerCase()];
                  if (!stats) {
                    return (
                      <div
                        className="col-span-2 text-right font-mono-tight text-xs text-[#cbd5e0]"
                        data-testid={`rank-gsc-${i}`}
                        title="No GSC impressions for this exact query in the last 28 days"
                      >
                        —
                      </div>
                    );
                  }
                  const impr = stats.impressions || 0;
                  const clicks = stats.clicks || 0;
                  const ctrPct = (stats.ctr || 0) * 100;
                  const gscPos = stats.position || 0;
                  return (
                    <div
                      className="col-span-2 text-right font-mono-tight"
                      data-testid={`rank-gsc-${i}`}
                      title={`${Math.round(impr)} impressions · ${Math.round(clicks)} clicks · ${ctrPct.toFixed(1)}% CTR · GSC avg pos ${gscPos.toFixed(1)}`}
                    >
                      <div className="text-[#1a202c] font-semibold">{fmt(impr)}</div>
                      <div className="text-[9px] text-[#a0aec0] -mt-0.5">
                        {Math.round(clicks)} clk · {ctrPct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })()}
                <div className="col-span-2 flex items-center justify-end" data-testid={`rank-features-${i}`}>
                  <SerpFeaturesCell present={r.serp_features} owned={r.owned_features} />
                </div>
                <div className="col-span-2 text-right text-xs font-mono-tight">
                  <RankDeltaCell delta={delta} />
                </div>
                <div className="col-span-2 text-right font-mono-tight text-xs">
                  {r.pending ? (
                    <span data-testid={`rank-delta-since-${i}`} className="text-[#a0aec0]">—</span>
                  ) : baseIsNew ? (
                    <span data-testid={`rank-delta-since-${i}`} className="text-[#48bb78] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#4fd1c5]/10">NEW</span>
                  ) : baseIsLost ? (
                    <span data-testid={`rank-delta-since-${i}`} className="text-[#f56565] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#fed7d7]/40">LOST</span>
                  ) : (
                    <RankDeltaCell delta={baseDelta} testid={`rank-delta-since-${i}`} />
                  )}
                </div>
                <div className="col-span-3 truncate text-xs font-mono-tight">
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={url}
                      data-testid={`rank-url-${i}`}
                      className="text-[#4a5568] hover:text-[#2c7a7b]"
                    >
                      {urlShort.length > 36 ? urlShort.slice(0, 36) + "…" : urlShort}
                    </a>
                  ) : r.pending ? (
                    <span className="text-[#a0aec0] italic">awaiting first crawl</span>
                  ) : (
                    <span className="text-[#a0aec0]">not ranking</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
